import { useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Archive, ArchiveRestore, Check, ClipboardList, Copy, MessageSquare, Pencil, Plus, Trash2, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard';
import { EstadoChip } from '@/components/rdt/estado-chip';
import { NroRdt } from '@/components/rdt/nro-rdt';
import { CicloVida } from '@/components/rdt/ciclo-vida';
import { ChequeosTabla } from '@/components/rdt/chequeos-tabla';
import {
  fFecha,
  fFechaCorta,
  fmtMonto,
  fmtPct,
  normalizarNro,
  perLabel,
  periodoRdt,
  procedenciaMonto,
  procedenciaPct,
  procedenciaReferencia,
  ultimoChequeo,
  type Procedencia,
} from '@/components/rdt/rdt-domain';
import { AvatarPersona } from '@/components/rdt/avatar-persona';
import { useActualizarRdt, type ActualizarRdtInput } from '@/hooks/use-rdt-mutations';
import { useMe } from '@/hooks/use-me';
import { useUsuarios } from '@/hooks/use-usuarios';
import { ApiError } from '@/lib/query-client';
import type { TipoPaso } from '@/components/rdt/rdt-domain';
import type { RdtDetalle, TipoRdt } from '@/types/api';

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message || fallback;
  if (err instanceof Error) return err.message;
  return fallback;
}

/** Texto del Popover de procedencia — «Del paso «X» · autor · fecha»,
 *  «Histórico (migrada de la app anterior)» o el aviso de "sin datos". */
function procedenciaTexto(label: string, proc: Procedencia): string {
  if (!proc) return 'Sin procedencia registrada.';
  if (proc.origen === 'historico') return 'Histórico (migrada de la app anterior).';
  return `${label} · ${proc.autor} · ${fFecha(proc.fecha)}`;
}

/** `claveHilo` de una procedencia — null en 'historico'/sin datos: ahí no
 *  hay a dónde scrollear, se conserva el Popover de siempre (puntos 8/14). */
function claveHiloDe(proc: Procedencia): string | null {
  return proc?.claveHilo ?? null;
}

/** Botón "ir al ítem del hilo" — mismo lugar que ocupaba el Popover de
 *  procedencia de la lápida; en vez de abrir un popover, hace scroll+resalta
 *  el ítem correspondiente en Actividad (decisión "scroll en todo", puntos
 *  8/14). Se usa SOLO cuando la procedencia es un paso real con `claveHilo`
 *  — 'historico' (sin destino) sigue con el Popover de siempre. `tooltip`
 *  reemplaza el `title` nativo de antes (Referencia truncada) — sin él, no
 *  envuelve en Tooltip. Subrayado punteado y sin padding (paridad con la
 *  rc.1, qa_visual_reui_2026-09-28.md ficha-1): el hover del `variant="ghost"`
 *  no alcanza como único affordance de que el valor es clickeable. */
function BotonIrAlHilo({
  clave,
  onIrAItem,
  className,
  tooltip,
  children,
}: {
  clave: string;
  onIrAItem?: (clave: string) => void;
  className?: string;
  tooltip?: string;
  children: ReactNode;
}) {
  const boton = (
    <Button
      type="button"
      variant="ghost"
      size="xs"
      onClick={() => onIrAItem?.(clave)}
      className={cn(
        'h-auto justify-start px-0 py-0 font-normal underline decoration-dotted decoration-border-strong underline-offset-[3px]',
        className,
      )}
    >
      {children}
    </Button>
  );
  if (!tooltip) return boton;
  return (
    <Tooltip>
      <TooltipTrigger render={boton} />
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

interface Props {
  rdt: RdtDetalle;
  actividadCount: number;
  onAbrirActividadMobile: () => void;
  onAbrirPaso: (tipo: Exclude<TipoPaso, 'comentario'>, hitoKey?: string) => void;
  /** Dispara el form reducido "Corregir hitos" (edicion-rdt.tsx) — la
   *  identidad (nro/responsable) ahora se edita INLINE acá mismo. */
  onEditar: () => void;
  onBorrar: () => void;
  onArchivar: () => void;
  /** contrato B1: scroll+resalta el ítem del hilo con esta clave (obra-detalle.tsx
   *  arma la clave `act-${itemKey}` y hace el scrollIntoView + highlight
   *  one-shot) — puntos 8/9/14. Opcional para no romper mientras el llamador
   *  no lo cablea todavía: sin él, los botones de procedencia no hacen nada. */
  onIrAItem?: (clave: string) => void;
  /** contrato B1: `true` mientras useArchivarRdt() está en vuelo — deshabilita
   *  el botón y cambia su label (punto 16, bug de "falta pending"). */
  archivando?: boolean;
  /** contrato B1: mismo valor que `rdt.finalizada` (RdtEstadoDerivado) — el
   *  llamador (obra-detalle.tsx) ya lo tiene a mano y lo pasa explícito;
   *  opcional con fallback al campo del propio `rdt` por si algún consumidor
   *  no lo pasa. */
  finalizada?: boolean;
  /** Decisión #11b v0.9.0: borrar la RDT entera es solo del creador o un
   *  admin (el backend devuelve 403 al resto) — el llamador lo calcula con
   *  `creadoPorId` + el user de sesión. Default true para no romper otros
   *  consumidores (el backend corta igual). */
  puedeBorrar?: boolean;
}

// Ficha central de una RDT (la "lápida"): "todo es por actividad" — cada
// dato de gestión (%, monto, período, referencia) es una PROYECCIÓN del
// último paso vivo del hilo, con su procedencia clickeable en un Popover
// (nunca hover-only: tiene que andar en tablet). La identidad (nro/
// responsable) se edita INLINE acá mismo (ajuste 2 de Juan); "Corregir
// hitos" sigue abriendo el form reducido de abajo (edicion-rdt.tsx).
export function Ficha({
  rdt,
  actividadCount,
  onAbrirActividadMobile,
  onAbrirPaso,
  onEditar,
  onBorrar,
  onArchivar,
  onIrAItem,
  archivando = false,
  finalizada = rdt.finalizada,
  puedeBorrar = true,
}: Props) {
  // R3: la lógica de copiar (con su estado "copiado" temporal) ya no se
  // duplica acá — un hook por botón, c-button-42.
  const avisarErrorCopia = () => toast.error('No se pudo copiar (permiso del navegador)');
  const { isCopied: copiadaReferencia, copyToClipboard: copiarReferencia } = useCopyToClipboard({ timeout: 1200, onError: avisarErrorCopia });
  const [editandoIdentidad, setEditandoIdentidad] = useState(false);
  const [nroEdit, setNroEdit] = useState('');
  const [responsableIdEdit, setResponsableIdEdit] = useState<number | null>(null);
  const [tipoEdit, setTipoEdit] = useState<TipoRdt>('Provisoria');
  const [notaEdit, setNotaEdit] = useState('');
  // Snapshot de los valores ORIGINALES al momento de ENTRAR en modo edición
  // — un ref, no reactivo: nunca se re-sincroniza si `rdt` cambia mientras
  // `editandoIdentidad` sigue activo (refetch de fondo, invalidación de OTRA
  // mutación, refetchOnWindowFocus). guardarEdicionIdentidad() calcula el
  // diff contra ESTE snapshot, nunca contra el `rdt` prop en vivo: si no,
  // un refetch que trajera un nro/responsable ya cambiado por otra persona
  // haría que el diff detectara "cambios" en campos que este usuario ni tocó
  // y los pisaría con el valor viejo que vio al abrir el form.
  const identidadOriginalRef = useRef<{ nro: string; responsableId: number | null; tipo: TipoRdt } | null>(null);
  const { data: me } = useMe();
  const { data: usuarios } = useUsuarios();
  const actualizarRdt = useActualizarRdt();
  const navigate = useNavigate();

  const uc = ultimoChequeo(rdt);
  const per = periodoRdt(rdt);
  const responsableNombre = rdt.responsable?.nombre ?? rdt.responsableHistorico ?? null;
  // Tipo editable SOLO si la RDT no tiene chequeos ni montos cargados todavía
  // (decisión de Juan, punto 3): una vez que hay gestión real encima, cambiar
  // Provisoria↔Definitiva dejaría datos huérfanos (chequeos sin sentido en
  // una Definitiva, montos sin sentido en una Provisoria). Espejo EXACTO del
  // guard del backend (review v0.9.0): el monto legacy congelado de una
  // migrada (montoHistorico) también bloquea — sin esto el Select aparecía
  // en migradas Definitivas "Calculado" sin pasos y el guardado daba 422
  // siempre.
  const puedeCambiarTipo = rdt.chequeos.length === 0 && rdt.montos.length === 0 && rdt.montoHistorico == null;

  const procMonto = rdt.tipo === 'Definitiva' ? procedenciaMonto(rdt) : null;
  const procRef = procedenciaReferencia(rdt);
  const procPct = rdt.tipo === 'Provisoria' ? procedenciaPct(rdt) : null;
  const periodoDefinitiva = procMonto?.origen === 'paso' && procMonto.periodo ? perLabel(procMonto.periodo) : null;
  // Bug de proyección (punto 7): el % de la lápida tiene que leer PRIMERO el
  // salto vivo (rdt.salto.valorPct, lo que la API ya manda) y caer a
  // rdt.indicePct (columna legacy) solo si no hay salto — mismo par/orden que
  // procedenciaPct() más arriba. Antes leía indicePct a secas: si saltaba con
  // %, pero indicePct no se había resincronizado, la lápida mostraba el %
  // viejo o directamente el hint de "todavía no saltó".
  const pctValue = rdt.salto?.valorPct ?? rdt.indicePct;
  // Punto 10 (Juan QA 2026-09-14, Definitivas de SOFSE/ADIF por FRI/%): el
  // bloque «Monto (neto, sin IVA)» de una Definitiva muestra lo que haya del
  // monto vigente — monto, %, o los dos. `rdt.salto` es SIEMPRE null fuera
  // de Provisorias, así que `pctValue` (arriba) ya se reduce exactamente a
  // `rdt.indicePct` acá — el % del vigente que el backend materializa
  // (§4 de la spec de montos), se reusa tal cual. La procedencia (Popover /
  // ir al hilo) sigue saliendo de `procMonto`/`claveHiloMonto`, sin cambios.
  const hayMontoODefinitivaPct = rdt.tipo === 'Definitiva' && (rdt.monto != null || pctValue != null);
  // Cada valor es una unidad inquebrantable (whitespace-nowrap, Juan en Safari
  // 2026-09-15 validando v0.13.1: con la ficha a 760 y la ventana angosta, el
  // «4,30 %» se partía en «4,30» arriba y «%» abajo). Si no entran los dos en
  // el renglón, el % baja ENTERO al siguiente (inline-block), nunca a mitad.
  const contenidoMontoDefinitiva = (
    <>
      {rdt.monto != null && <span className="whitespace-nowrap">{fmtMonto.format(rdt.monto)}</span>}
      {pctValue != null && (
        <span className={`inline-block whitespace-nowrap${rdt.monto != null ? ' ml-1.5 text-sm font-medium text-text-tertiary' : ''}`}>
          {fmtPct.format(pctValue)} %
        </span>
      )}
    </>
  );
  const claveHiloPct = claveHiloDe(procPct);
  const claveHiloMonto = claveHiloDe(procMonto);
  const claveHiloRef = claveHiloDe(procRef);

  // Botonera de escritura: solo gestor/admin la ve (los viewers no tienen
  // los endpoints habilitados — ocultar, no deshabilitar, hallazgo del
  // review adversarial 2026-07-10). Editar/Borrar además se ocultan con la
  // RDT archivada: son acciones que el ciclo de vida ya no permite ahí (el
  // backend las rechaza con 422/lo hará — la capa UI no debe ofrecerlas).
  const puedeEscribir = me?.rol === 'gestor' || me?.rol === 'admin';
  // Archivado universal (Cierres finales, 2026-07-11): es una marca de
  // visibilidad, no una transición del ciclo — se ofrece desde CUALQUIER
  // estado, SUPERSEDE la guarda v0.7.0 "solo Sin salto/A chequear".
  const puedeArchivar = puedeEscribir;
  const puedeEditarOBorrar = puedeEscribir && !rdt.archivada;
  // Deep-link admin-only a la pantalla de Auditoría filtrada por esta rede
  // (Tramo D, `context/design_auditoria.md` §6): visible, no deshabilitado —
  // mismo criterio de "ocultar, no deshabilitar" que puedeEscribir de arriba.
  const esAdmin = me?.rol === 'admin';
  // Mismo criterio para los empty states accionables de la lápida ("+ Monto
  // informado", "+ Expediente"): con la RDT archivada pasan a texto estático
  // (ver más abajo `puedeEscribir && !rdt.archivada` en cada uno) — el CTA
  // "Crear la Rede siguiente" (obra-detalle.tsx) y el knob "Completar →" del
  // stepper (ciclo-vida.tsx) ya se ocultan solos con `rdt.archivada`.

  function empezarEdicionIdentidad(): void {
    identidadOriginalRef.current = { nro: rdt.nro, responsableId: rdt.responsable?.id ?? null, tipo: rdt.tipo };
    setNroEdit(rdt.nro);
    setResponsableIdEdit(rdt.responsable?.id ?? null);
    setTipoEdit(rdt.tipo);
    setNotaEdit('');
    setEditandoIdentidad(true);
  }

  function cancelarEdicionIdentidad(): void {
    setEditandoIdentidad(false);
    identidadOriginalRef.current = null;
  }

  function guardarEdicionIdentidad(): void {
    const nroTrim = nroEdit.trim();
    if (!nroTrim) return;
    // Fallback defensivo (nunca debería faltar: se setea al entrar en modo
    // edición) — si faltara, cae de nuevo al `rdt` en vivo en vez de romper.
    const original = identidadOriginalRef.current ?? { nro: rdt.nro, responsableId: rdt.responsable?.id ?? null, tipo: rdt.tipo };
    const patch: ActualizarRdtInput = { cc: rdt.cc, id: rdt.id };
    let hayCambios = false;
    // Espejo del backend (decisión de Juan 2026-09-14, caso «4» vs «04» de la
    // 1426): se compara y se manda el nro NORMALIZADO — retipear «4» sobre una
    // «04» no es un cambio, y no dispara un PUT redundante. El backend
    // normaliza igual por su cuenta; acá es para no mandar ruido.
    const nroNorm = normalizarNro(nroTrim);
    if (nroNorm !== original.nro) {
      patch.nro = nroNorm;
      hayCambios = true;
    }
    if (responsableIdEdit !== original.responsableId) {
      patch.responsableId = responsableIdEdit;
      hayCambios = true;
    }
    if (puedeCambiarTipo && tipoEdit !== original.tipo) {
      patch.tipo = tipoEdit;
      hayCambios = true;
    }
    const notaTrim = notaEdit.trim();
    // La nota viaja SOLO junto a un cambio real: el backend la adjunta a los
    // ecos de nro/responsable/tipo, y sin cambio no hay eco donde guardarla
    // (el PUT sin cambios da 400 — review v0.9.0). Para dejar contexto suelto
    // sin tocar nada está el comentario del hilo.
    if (hayCambios && notaTrim) {
      patch.nota = notaTrim;
    }
    if (!hayCambios) {
      if (notaTrim) {
        toast('La nota va junto a un cambio de identidad — para dejar solo una nota usá un comentario en la Actividad.');
        return;
      }
      setEditandoIdentidad(false);
      return;
    }
    actualizarRdt.mutate(patch, {
      onSuccess: () => {
        toast.success('Cambios guardados');
        setEditandoIdentidad(false);
        identidadOriginalRef.current = null;
      },
      // El 422 del backend ("tiene chequeos/montos cargados", si igual
      // llegara a mandarse un tipo no permitido) se muestra tal cual, sin
      // envolverlo — errorMessage() ya reenvía err.message de la ApiError.
      onError: err => toast.error(errorMessage(err, 'No se pudo guardar')),
    });
  }

  return (
    <div className="rounded-[11px] border border-border-subtle bg-surface-card shadow-[0_2px_8px_rgba(20,20,22,0.07)]">
      <div className="flex flex-wrap items-center gap-2.5 border-b border-border-subtle px-[18px] pb-[13px] pt-[15px]">
        <h2 className="m-0 flex min-w-0 items-baseline gap-2 text-base font-[650] tracking-[-0.01em]">
          {editandoIdentidad ? (
            <span className="flex items-center gap-1.5">
              <span className="font-mono text-muted-foreground">Rede #</span>
              <Input
                value={nroEdit}
                onChange={e => setNroEdit(e.target.value)}
                aria-label="Número de RDT"
                autoFocus
                className="h-7 w-[112px] font-mono text-sm"
              />
            </span>
          ) : (
            <span className="font-mono">
              Rede <NroRdt nro={rdt.nro} tipo={rdt.tipo} />
            </span>
          )}
          {editandoIdentidad && puedeCambiarTipo ? (
            <Select value={tipoEdit} onValueChange={v => v && setTipoEdit(v as TipoRdt)}>
              <SelectTrigger aria-label="Tipo" className="h-7 w-[116px] text-xs">
                <SelectValue>{(v: string) => v}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Provisoria">Provisoria</SelectItem>
                <SelectItem value="Definitiva">Definitiva</SelectItem>
              </SelectContent>
            </Select>
          ) : editandoIdentidad ? (
            <Tooltip>
              <TooltipTrigger render={<span className="text-xs font-medium text-muted-foreground" />}>{rdt.tipo}</TooltipTrigger>
              <TooltipContent>No editable: ya tiene chequeos o montos cargados</TooltipContent>
            </Tooltip>
          ) : (
            <span className="text-xs font-medium text-muted-foreground">{rdt.tipo}</span>
          )}
          {!editandoIdentidad && puedeEditarOBorrar && (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    onClick={empezarEdicionIdentidad}
                    aria-label="Editar número y responsable"
                    className="flex-none text-text-tertiary hover:text-foreground"
                  />
                }
              >
                <Pencil className="h-3 w-3" />
              </TooltipTrigger>
              <TooltipContent>Editar número y responsable</TooltipContent>
            </Tooltip>
          )}
        </h2>
        <div className="ml-auto flex items-center gap-[7px]">
          {editandoIdentidad ? (
            <>
              <Button type="button" variant="outline" size="sm" onClick={cancelarEdicionIdentidad} disabled={actualizarRdt.isPending}>
                Cancelar
              </Button>
              <Button type="button" size="sm" onClick={guardarEdicionIdentidad} disabled={actualizarRdt.isPending || !nroEdit.trim()}>
                {actualizarRdt.isPending ? 'Guardando…' : 'Guardar'}
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onAbrirActividadMobile}
                aria-label="Ver actividad"
                className="hidden max-[1279px]:inline-flex"
              >
                <MessageSquare className="h-3.5 w-3.5" />
                {actividadCount}
              </Button>
              <EstadoChip estado={rdt.estado} esperandoPoliza={rdt.esperandoPoliza} />
            </>
          )}
        </div>
      </div>

      {editandoIdentidad && (
        <div className="border-b border-border-subtle px-[18px] py-2.5">
          <Field className="max-w-md gap-[3px]">
            <FieldLabel htmlFor="ed-identidad-nota" className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              Nota (opcional)
            </FieldLabel>
            <Input
              id="ed-identidad-nota"
              value={notaEdit}
              onChange={e => setNotaEdit(e.target.value)}
              placeholder="Ej: corregido el número, venía mal tipeado"
            />
          </Field>
        </div>
      )}

      <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-x-[22px] gap-y-3.5 px-[18px] py-[15px]">
        <div className="min-w-0">
          <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">Responsable</div>
          {editandoIdentidad ? (
            <Select
              value={responsableIdEdit != null ? String(responsableIdEdit) : 'sin-asignar'}
              onValueChange={v => setResponsableIdEdit(v === 'sin-asignar' ? null : Number(v))}
            >
              <SelectTrigger aria-label="Responsable" className="h-8 w-full text-xs">
                <SelectValue>
                  {(v: string) => {
                    if (!v || v === 'sin-asignar') return 'Sin asignar';
                    const u = usuarios?.find(u2 => String(u2.id) === v);
                    if (!u) return '…';
                    return (
                      <span className="flex items-center gap-2">
                        <AvatarPersona nombre={u.nombre} fotoUrl={u.fotoUrl} size={18} />
                        {u.nombre}
                      </span>
                    );
                  }}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sin-asignar">Sin asignar</SelectItem>
                {(usuarios ?? []).map(u => (
                  <SelectItem key={u.id} value={String(u.id)}>
                    <span className="flex items-center gap-2">
                      <AvatarPersona nombre={u.nombre} fotoUrl={u.fotoUrl} size={18} />
                      {u.nombre}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : responsableNombre ? (
            <div className="flex min-w-0 items-center gap-[7px] text-[13.5px]">
              <AvatarPersona nombre={responsableNombre} fotoUrl={rdt.responsable?.fotoUrl ?? null} size={21} />
              <span className="truncate">{responsableNombre}</span>
            </div>
          ) : (
            <div className="text-[13.5px] text-text-tertiary">—</div>
          )}
        </div>

        <div className="min-w-0">
          <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">Período</div>
          <div className="tabular-nums text-[13.5px]">
            {rdt.tipo === 'Definitiva' ? (
              periodoDefinitiva ? (
                claveHiloMonto ? (
                  <BotonIrAlHilo clave={claveHiloMonto} onIrAItem={onIrAItem}>
                    {periodoDefinitiva}
                  </BotonIrAlHilo>
                ) : (
                  <Popover>
                    <PopoverTrigger
                      render={
                        <Button
                          type="button"
                          variant="ghost"
                          size="xs"
                          className="h-auto justify-start px-0 py-0 font-normal underline decoration-dotted decoration-border-strong underline-offset-[3px]"
                        />
                      }
                    >
                      {periodoDefinitiva}
                    </PopoverTrigger>
                    <PopoverContent>{procedenciaTexto('Del paso «Monto informado»', procMonto)}</PopoverContent>
                  </Popover>
                )
              ) : (
                <span className="text-text-tertiary">sin período</span>
              )
            ) : per ? (
              claveHiloPct ? (
                <BotonIrAlHilo clave={claveHiloPct} onIrAItem={onIrAItem}>
                  {per}
                </BotonIrAlHilo>
              ) : (
                <Popover>
                  <PopoverTrigger
                    render={
                      <Button
                        type="button"
                        variant="ghost"
                        size="xs"
                        className="h-auto justify-start px-0 py-0 font-normal underline decoration-dotted decoration-border-strong underline-offset-[3px]"
                      />
                    }
                  >
                    {per}
                  </PopoverTrigger>
                  <PopoverContent>
                    {procedenciaTexto(
                      procPct?.origen === 'paso' ? `Del chequeo que saltó (per. ${perLabel(procPct.periodo!)})` : 'Del chequeo que saltó',
                      procPct,
                    )}
                  </PopoverContent>
                </Popover>
              )
            ) : uc ? (
              <Tooltip>
                <TooltipTrigger render={<span className="text-text-tertiary" />}>chq. hasta {perLabel(uc.periodo)}</TooltipTrigger>
                <TooltipContent>{`Chequeada hasta ${perLabel(uc.periodo)} — sin salto`}</TooltipContent>
              </Tooltip>
            ) : (
              <span className="text-text-tertiary">sin chequeos</span>
            )}
          </div>
        </div>

        {rdt.tipo === 'Provisoria' ? (
          <div className="min-w-0">
            <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">Porcentaje</div>
            <div className="text-xl font-[650] tabular-nums tracking-[-0.01em]">
              {pctValue != null ? (
                claveHiloPct ? (
                  <BotonIrAlHilo clave={claveHiloPct} onIrAItem={onIrAItem}>
                    {fmtPct.format(pctValue)} <span className="text-xs font-medium text-text-tertiary">%</span>
                  </BotonIrAlHilo>
                ) : (
                  <Popover>
                    <PopoverTrigger
                      render={
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-auto justify-start px-0 py-0 text-xl font-[650] underline decoration-dotted decoration-border-strong underline-offset-[3px]"
                        />
                      }
                    >
                      {fmtPct.format(pctValue)} <span className="text-xs font-medium text-text-tertiary">%</span>
                    </PopoverTrigger>
                    <PopoverContent>
                      {procedenciaTexto(
                        procPct?.origen === 'paso' ? `Del chequeo que saltó (per. ${perLabel(procPct.periodo!)})` : 'Del chequeo que saltó',
                        procPct,
                      )}
                    </PopoverContent>
                  </Popover>
                )
              ) : rdt.salto ? (
                // Bug punto 7: hay salto pero el chequeo que saltó no informó
                // % (valorPct null) — antes esto mostraba el hint de "todavía
                // no saltó", que era directamente falso.
                <span className="text-sm font-normal text-text-tertiary">Saltó sin % informado</span>
              ) : (
                <span className="text-sm font-normal text-text-tertiary">se completa con el chequeo que salta</span>
              )}
            </div>
          </div>
        ) : (
          <div className="min-w-0">
            <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              {/* Definitivas de SOFSE/ADIF se gestionan por % en vez de monto
                  (Juan, QA 2026-09-14) — la etiqueta pasa a «Monto
                  informado» (el nombre del paso) cuando no hay monto, solo
                  %; con monto (con o sin % al lado) sigue diciendo lo que
                  es: neto, sin IVA. */}
              {rdt.monto == null && pctValue != null ? 'Monto informado' : 'Monto (neto, sin IVA)'}
            </div>
            <div className="text-xl font-[650] tabular-nums tracking-[-0.01em]">
              {hayMontoODefinitivaPct ? (
                claveHiloMonto ? (
                  <BotonIrAlHilo clave={claveHiloMonto} onIrAItem={onIrAItem}>
                    {contenidoMontoDefinitiva}
                  </BotonIrAlHilo>
                ) : (
                  <Popover>
                    <PopoverTrigger
                      render={
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-auto justify-start px-0 py-0 text-xl font-[650] underline decoration-dotted decoration-border-strong underline-offset-[3px]"
                        />
                      }
                    >
                      {contenidoMontoDefinitiva}
                    </PopoverTrigger>
                    <PopoverContent>{procedenciaTexto('Del paso «Monto informado»', procMonto)}</PopoverContent>
                  </Popover>
                )
              ) : puedeEscribir && !rdt.archivada && !finalizada ? (
                // U-11 (rc.6, qa_visual_reui_2026-09-28.md): el botón vacío
                // recupera el borde punteado de «sin cargar» que tenía en la
                // rc.1 — `variant="outline"` de ReUI trae borde sólido, se
                // suma `border-dashed` a mano (mismo patrón de
                // rdt-list-card.tsx:139 y estado-obra-badge.tsx:62). El hover
                // de texto usa el modificador completo
                // `not-aria-disabled:hover:text-primary` — `hover:` a secas
                // pierde por especificidad contra el
                // `not-aria-disabled:hover:text-foreground` de la variante
                // outline.
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onAbrirPaso('monto')}
                  className="border-dashed text-muted-foreground hover:border-primary/50 not-aria-disabled:hover:text-primary"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Monto informado
                </Button>
              ) : (
                <span className="text-sm font-normal text-text-tertiary">Sin cargar</span>
              )}
            </div>
          </div>
        )}

        <div className="min-w-0">
          <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">Referencia</div>
          <div className="group/ref flex min-w-0 items-center gap-[7px] font-mono text-xs text-muted-foreground">
            {rdt.referencia ? (
              <>
                {claveHiloRef ? (
                  <BotonIrAlHilo clave={claveHiloRef} onIrAItem={onIrAItem} tooltip={rdt.referencia} className="min-w-0 truncate">
                    {rdt.referencia}
                  </BotonIrAlHilo>
                ) : (
                  <Popover>
                    <Tooltip>
                      <TooltipTrigger
                        render={
                          <PopoverTrigger
                            render={
                              <Button
                                type="button"
                                variant="ghost"
                                size="xs"
                                className="h-auto min-w-0 justify-start truncate px-0 py-0 font-normal underline decoration-dotted decoration-border-strong underline-offset-[3px]"
                              />
                            }
                          />
                        }
                      >
                        {rdt.referencia}
                      </TooltipTrigger>
                      <TooltipContent>{rdt.referencia}</TooltipContent>
                    </Tooltip>
                    <PopoverContent>{procedenciaTexto('Del paso «Expediente»', procRef)}</PopoverContent>
                  </Popover>
                )}
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        onClick={() => copiarReferencia(rdt.referencia!)}
                        aria-label={copiadaReferencia ? 'Copiado' : 'Copiar referencia'}
                        className={cn(
                          'flex-none text-text-tertiary opacity-0 transition-opacity focus-visible:opacity-100 group-hover/ref:opacity-100',
                          copiadaReferencia && 'text-success opacity-100',
                        )}
                      />
                    }
                  >
                    {copiadaReferencia ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  </TooltipTrigger>
                  <TooltipContent>{copiadaReferencia ? 'Copiado' : 'Copiar referencia'}</TooltipContent>
                </Tooltip>
              </>
            ) : puedeEscribir && !rdt.archivada ? (
              // U-11: mismo fix que «+ Monto informado» arriba — borde
              // punteado en vez del sólido default de `outline`, con el
              // mismo guard completo `not-aria-disabled:hover:text-primary`
              // en el hover de texto, por la misma razón de especificidad.
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onAbrirPaso('expediente')}
                className="border-dashed font-sans text-muted-foreground hover:border-primary/50 not-aria-disabled:hover:text-primary"
              >
                <Plus className="h-3.5 w-3.5" />
                Expediente
              </Button>
            ) : (
              <span className="text-text-tertiary">—</span>
            )}
          </div>
        </div>
      </div>

      <div className="px-[18px] pb-[15px] pt-1">
        <div className="mb-3 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">Ciclo de vida</div>
        <CicloVida rdt={rdt} onAbrirPaso={onAbrirPaso} onIrAItem={onIrAItem} />
        {rdt.estado === 'Sin salto' && uc && (
          <p className="mt-2.5 text-xs text-text-tertiary">
            Chequeada hasta <b className="font-semibold text-muted-foreground">{perLabel(uc.periodo)}</b> sin salto — el ciclo sigue con los
            índices del próximo período.
          </p>
        )}
        {rdt.estado === 'A chequear' && (
          <p className="mt-2.5 text-xs text-text-tertiary">
            Sin chequeos registrados — la RDT está <b className="font-semibold text-muted-foreground">A chequear</b>.
          </p>
        )}
        {rdt.estado === 'A calcular' && (
          <p className="mt-2.5 text-xs text-text-tertiary">
            Sin montos informados — la RDT está <b className="font-semibold text-muted-foreground">A calcular</b>.
          </p>
        )}
      </div>

      {/* Solo Provisorias: las Definitivas no chequean índices (informan
          montos por el hilo) — mostrar la tabla acá invitaría a un paso que
          la paleta, con razón, no ofrece (hallazgo del review visual C). */}
      {rdt.tipo === 'Provisoria' && <ChequeosTabla rdt={rdt} />}

      <div className="flex flex-wrap items-center gap-x-2 gap-y-2.5 border-t border-border-subtle px-[18px] py-3">
        <span className="mr-auto max-w-full overflow-hidden truncate whitespace-nowrap text-[11px] text-text-tertiary">
          {rdt.migrada ? 'Migrada del histórico' : 'Creada en RedeApp'} · creada {fFechaCorta(rdt.creadoAt)}
        </span>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          {esAdmin && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/auditoria?rdt=${rdt.id}`)}
              className="gap-1.5 text-muted-foreground"
            >
              <ClipboardList className="h-3.5 w-3.5" />
              Auditoría
            </Button>
          )}
          {puedeArchivar && (
            <Button type="button" variant="outline" size="sm" onClick={onArchivar} disabled={archivando} className="gap-1.5">
              {rdt.archivada ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
              {archivando ? (rdt.archivada ? 'Desarchivando…' : 'Archivando…') : rdt.archivada ? 'Desarchivar' : 'Archivar'}
            </Button>
          )}
          {puedeEditarOBorrar && puedeBorrar && (
            // Borrar vuelve a fantasma (ghost), como en la rc.1 (decisión de
            // Juan, 28/9, qa_visual_reui_2026-09-28.md §7.2: el fondo
            // permanente de `variant="destructive"` resultaba agresivo para
            // una acción con confirm-dialog propio). Texto e ícono son rojos
            // FIJOS; el hover solo suma el fondo rosado tenue, con el guard
            // completo `not-aria-disabled:hover:*` (+ `dark:`) para ganarle
            // al hover default de ghost.
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onBorrar}
              className="gap-1.5 text-destructive not-aria-disabled:hover:bg-destructive/10 dark:not-aria-disabled:hover:bg-destructive/10 not-aria-disabled:hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Borrar
            </Button>
          )}
          {puedeEditarOBorrar && (
            <Button type="button" variant="outline" size="sm" onClick={onEditar} className="gap-1.5">
              <Wrench className="h-3.5 w-3.5" />
              Corregir hitos
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
